import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { MAIN_SITE_BRANDING } from '../lib/mainSiteBranding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Read a `?cdp=<client_id>` query param when on the customer-facing site.
 * When present we treat the visit as if it landed on that tenant's verified
 * custom domain — used by POS Settings → Site Branding "Preview my site"
 * to let owners walk their pages with their branding without DNS.
 */
function getCdpClientId() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const cdp = params.get('cdp');
  return cdp || null;
}

/**
 * Pick out a usable socials map if at least one link is set, else null.
 */
function pickSocials(b) {
  if (!b) return null;
  if (!b.facebook && !b.instagram && !b.twitter && !b.tiktok && !b.youtube) return null;
  return {
    facebook: b.facebook || '',
    instagram: b.instagram || '',
    twitter: b.twitter || '',
    tiktok: b.tiktok || '',
    youtube: b.youtube || '',
  };
}

/**
 * Customer-site branding hook — single source of truth for header,
 * footer, click-to-call, warranty copy, socials, and logo.
 *
 * - **Main site** (no `siteClient`, no `?cdp=`): fetches `/api/main-site-branding`
 *   (super_admin-edited corporate values). Falls back to hardcoded
 *   `MAIN_SITE_BRANDING` if the endpoint fails. NEVER reads any tenant's branding.
 * - **Custom domain** (`siteClient` resolved from `/api/clients/by-domain`):
 *   fetches the tenant's branding record and exposes their info.
 * - **CDP preview** (`?cdp=<client_id>` on the main site): same as custom
 *   domain — used by POS owners to preview before DNS.
 */
export function useSiteBranding() {
  const { siteClient } = useAuth();
  const [tenantData, setTenantData] = useState(null);
  const [mainData, setMainData] = useState(null);
  const [previewClientId] = useState(getCdpClientId);

  const effectiveClientId = siteClient?.client_id || previewClientId;
  const isCustomDomain = !!siteClient;
  const isPreview = !siteClient && !!previewClientId;
  const isTenant = isCustomDomain || isPreview;

  // Tenant branding fetch — only when on a custom domain or preview.
  useEffect(() => {
    if (!effectiveClientId) {
      setTenantData(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API_URL}/api/clients/${effectiveClientId}/branding`)
      .then(({ data: d }) => { if (!cancelled) setTenantData(d); })
      .catch(() => { if (!cancelled) setTenantData(null); });
    return () => { cancelled = true; };
  }, [effectiveClientId]);

  // Main-site branding fetch — only when NOT on a tenant site.
  useEffect(() => {
    if (isTenant) {
      setMainData(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API_URL}/api/main-site-branding`)
      .then(({ data: d }) => { if (!cancelled) setMainData(d); })
      .catch(() => { if (!cancelled) setMainData(null); });
    return () => { cancelled = true; };
  }, [isTenant]);

  // Resolve based on mode.
  if (isTenant) {
    const tenantSocials = pickSocials(tenantData?.branding);
    const tenantName = tenantData?.name || siteClient?.name || null;
    const tenantPhone = tenantData?.contact_phone || siteClient?.contact_phone || null;
    const tenantEmail = tenantData?.contact_email || siteClient?.contact_email || null;
    const tenantLogo = tenantData?.branding?.logo_url || siteClient?.branding?.logo_url || null;
    const warrantyDays = Number(tenantData?.default_warranty_service) || 90;
    return {
      isCustomDomain: true,
      isPreview,
      clientId: effectiveClientId,
      tenantName: tenantName || MAIN_SITE_BRANDING.name,
      phone: tenantPhone || MAIN_SITE_BRANDING.phone,
      email: tenantEmail || MAIN_SITE_BRANDING.email,
      logoUrl: tenantLogo || MAIN_SITE_BRANDING.logoUrl,
      socials: tenantSocials || MAIN_SITE_BRANDING.socials,
      warrantyDays,
      warrantyText: (article = '') => `${article ? `${article} ` : ''}${warrantyDays}-day warranty`,
      warrantyLabel: () => `${warrantyDays}-Day Warranty`,
    };
  }

  // Main site — use backend-edited values with hardcoded fallbacks.
  const mainSocials = pickSocials(mainData?.branding);
  return {
    isCustomDomain: false,
    isPreview: false,
    clientId: null,
    tenantName: mainData?.name || MAIN_SITE_BRANDING.name,
    phone: mainData?.contact_phone || MAIN_SITE_BRANDING.phone,
    email: mainData?.contact_email || MAIN_SITE_BRANDING.email,
    logoUrl: mainData?.branding?.logo_url || MAIN_SITE_BRANDING.logoUrl,
    socials: mainSocials || MAIN_SITE_BRANDING.socials,
    warrantyDays: 90,
    warrantyText: (article = '') => `${article ? `${article} ` : ''}90-day warranty`,
    warrantyLabel: () => '90-Day Warranty',
  };
}
